import React from 'react';
import {
  Container, Typography, Box, Accordion, AccordionSummary, AccordionDetails,
  Card, CardContent, Chip, Divider, List, ListItem, ListItemIcon, ListItemText,
} from '@mui/material';
import {
  ExpandMore,
  Timeline,
  PlaylistAdd,
  TrendingUp,
  AccountBalance,
  Psychology,
  Email,
  HelpOutline,
  CheckCircleOutline,
  Info,
} from '@mui/icons-material';

const Help = () => {
  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 6 }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
        How It Works
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Everything you need to know about using SMPL Analysis for automated stock trading.
      </Typography>

      {/* Quick Start */}
      <Card sx={{ mb: 4, background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)', color: 'white' }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
            🚀 Quick Start — 3 Steps
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Chip label="1" size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 700 }} />
              <Typography><strong>Add stocks</strong> to your Watchlist — this tells the system what to analyze and trade</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Chip label="2" size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 700 }} />
              <Typography><strong>System generates signals</strong> daily — BUY, SELL, or HOLD with AI-powered analysis</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Chip label="3" size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 700 }} />
              <Typography><strong>Automated trading executes</strong> — you receive email alerts for every trade placed</Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Accordion Sections */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Timeline sx={{ mr: 2, color: 'primary.main' }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>How the System Works</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" paragraph>
            SMPL Analysis runs a fully automated pipeline every day:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon><CheckCircleOutline color="success" fontSize="small" /></ListItemIcon>
              <ListItemText primary="Stock prices fetched every 15 minutes during market hours" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleOutline color="success" fontSize="small" /></ListItemIcon>
              <ListItemText primary="20+ technical indicators calculated (RSI, MACD, EMA, Bollinger Bands, etc.)" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleOutline color="success" fontSize="small" /></ListItemIcon>
              <ListItemText primary="10 trading strategies evaluated per stock — best strategy selected via backtesting" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleOutline color="success" fontSize="small" /></ListItemIcon>
              <ListItemText primary="AI analysis using news, earnings, and macro context to validate signals" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleOutline color="success" fontSize="small" /></ListItemIcon>
              <ListItemText primary="Trading executed 3× daily (9:45 AM, 12:01 PM, 3:30 PM ET)" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleOutline color="success" fontSize="small" /></ListItemIcon>
              <ListItemText primary="Risk management runs every 30 minutes — trailing stops protect every position" />
            </ListItem>
          </List>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            The evening pipeline (8 PM ET) generates fresh signals and AI analysis for the next trading day.
            You receive a daily email report summarizing all signal changes and portfolio performance.
          </Typography>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <PlaylistAdd sx={{ mr: 2, color: 'primary.main' }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Watchlist</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" paragraph>
            Your watchlist is the foundation of everything. Only stocks on your watchlist get analyzed, receive trading signals, and are eligible for automated trading.
          </Typography>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>Adding Stocks</Typography>
          <Typography variant="body2" paragraph>
            Click "Add Stock" on the Watchlist page and enter a ticker symbol (e.g., AAPL, TSLA, MSFT).
            The system immediately fetches 5 years of historical data and starts computing indicators.
            Within minutes, you'll see a trading signal appear.
          </Typography>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>Auto-Discovery</Typography>
          <Typography variant="body2" paragraph>
            The system also automatically discovers new trading candidates from daily market movers.
            Stocks appearing as top gainers or most active for 3+ days get added to the watchlist automatically
            (filtered by $20 minimum price and $10B minimum market cap to avoid penny stocks).
          </Typography>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>Removing Stocks</Typography>
          <Typography variant="body2">
            Click the delete icon next to any stock to remove it. If you have an open position,
            the stock stays protected by risk management but won't receive new BUY signals.
          </Typography>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <TrendingUp sx={{ mr: 2, color: 'primary.main' }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Trading Signals</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Signal Types</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            <Chip label="BUY" color="success" size="small" />
            <Typography variant="body2" sx={{ lineHeight: '24px' }}>— Strong upward momentum, consider entering a position</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            <Chip label="SELL" color="error" size="small" />
            <Typography variant="body2" sx={{ lineHeight: '24px' }}>— Downward momentum or technical breakdown, exit the position</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            <Chip label="HOLD" color="warning" size="small" />
            <Typography variant="body2" sx={{ lineHeight: '24px' }}>— No clear direction, maintain current position (or no action if not holding)</Typography>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Confidence Score (0–100%)</Typography>
          <Typography variant="body2" paragraph>
            Each signal comes with a confidence score indicating how strongly the indicators support the recommendation:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText primary="≥ 80% — Very Strong" secondary="Multiple indicators aligned. High conviction." />
            </ListItem>
            <ListItem>
              <ListItemText primary="65–79% — Strong" secondary="Good setup with most conditions met." />
            </ListItem>
            <ListItem>
              <ListItemText primary="50–64% — Moderate" secondary="Mixed signals. System requires ≥65% to execute a BUY." />
            </ListItem>
            <ListItem>
              <ListItemText primary="< 50% — Weak" secondary="No clear direction. System will not trade." />
            </ListItem>
          </List>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Strategy Names</Typography>
          <Typography variant="body2" paragraph>
            The system evaluates 10 strategies and picks the best one for each stock based on 2-year backtesting:
          </Typography>
          <Typography variant="body2" component="div">
            <ul style={{ paddingLeft: 20, margin: 0 }}>
              <li><strong>Trend Momentum</strong> — EMA crossovers + RSI + MACD alignment</li>
              <li><strong>Mean Reversion</strong> — Bollinger Band bounces for oversold/overbought stocks</li>
              <li><strong>Breakout</strong> — Price breaking above/below recent highs/lows with volume</li>
              <li><strong>MACD Divergence</strong> — MACD crossovers and continuation patterns</li>
              <li><strong>Multi-Indicator Consensus</strong> — Requires 3+ indicators to agree</li>
              <li><strong>Volume Momentum</strong> — Price moves confirmed by unusual volume</li>
              <li><strong>Support/Resistance</strong> — Trading off key price levels</li>
              <li><strong>Volatility Expansion</strong> — Bollinger squeeze breakouts</li>
              <li><strong>Gap Trading</strong> — Opening gap patterns</li>
              <li><strong>Momentum Oscillator</strong> — RSI + Williams %R + CCI combination</li>
            </ul>
          </Typography>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <AccountBalance sx={{ mr: 2, color: 'primary.main' }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Portfolio & Risk Management</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Position Sizing</Typography>
          <Typography variant="body2" paragraph>
            Each position is sized at approximately 2.5% of your portfolio value, adjusted by sector weight
            and signal confidence. Higher confidence signals get slightly larger positions.
          </Typography>

          <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>Trailing Stops</Typography>
          <Typography variant="body2" paragraph>
            Every BUY order automatically gets a trailing stop placed. The stop percentage is calculated
            from the stock's volatility (ATR × 3, minimum 5%). As the stock price rises, the stop
            moves up with it — locking in gains while giving the stock room to breathe.
          </Typography>

          <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>Portfolio Limits</Typography>
          <List dense>
            <ListItem>
              <ListItemText primary="Max single position: 15% of portfolio" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Max sector concentration: 40% of portfolio" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Drawdown circuit breaker: 15% from peak (halts all new BUYs)" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Daily loss halt: 3% from start of day" />
            </ListItem>
          </List>

          <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>P&L Tracking</Typography>
          <Typography variant="body2">
            Every closed trade (SELL signal or trailing stop fill) records the realized profit/loss.
            View your trade history and cumulative P&L on the P&L page.
          </Typography>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Psychology sx={{ mr: 2, color: 'primary.main' }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>AI Analysis</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" paragraph>
            Each stock gets a daily AI-powered analysis that considers:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText primary="Technical indicators" secondary="RSI, MACD, EMA crossovers, Bollinger Bands, momentum" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Recent news" secondary="Stock-specific and macro news from the last 5 days, ranked by relevance" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Earnings context" secondary="Upcoming earnings dates, recent EPS surprises, quarter-over-quarter trends" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Market regime" secondary="Bull/Cautious/Volatile/Bear classification based on VIX and S&P 500" />
            </ListItem>
          </List>
          <Typography variant="body2" sx={{ mt: 1 }}>
            The AI recommendation is blended with the technical signal to produce a final combined signal.
            When they agree, confidence increases. When they disagree, the more conservative view wins.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Click on any stock to see its full AI analysis on the Stock Detail page.
          </Typography>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Email sx={{ mr: 2, color: 'primary.main' }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Email Notifications</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" paragraph>
            You receive two types of automated emails:
          </Typography>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Daily Report (evening)</Typography>
          <Typography variant="body2" paragraph>
            Summarizes today's BUY and SELL signals, portfolio performance, and signal changes.
            HOLD signals are not listed individually — just a count with a link to view the full watchlist.
          </Typography>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Trade Execution Alerts (real-time)</Typography>
          <Typography variant="body2">
            Sent immediately when a BUY or SELL order executes. Includes the strategy used,
            AI reasoning, confidence score, and a link to the stock's full analysis page.
          </Typography>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <HelpOutline sx={{ mr: 2, color: 'primary.main' }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>FAQ</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>When are signals updated?</Typography>
          <Typography variant="body2" paragraph>
            Signals are generated 3× daily (9:45 AM, 12:01 PM, 3:30 PM ET) during market hours,
            plus a comprehensive evening run at 8 PM ET that includes fresh AI analysis.
          </Typography>

          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>Why is my stock showing HOLD?</Typography>
          <Typography variant="body2" paragraph>
            HOLD means there's no strong signal in either direction. The indicators are mixed or neutral.
            This is the most common signal — most stocks are in HOLD most of the time. The system
            only trades when there's a clear, high-confidence BUY or SELL setup.
          </Typography>

          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>How do I stop trading a stock?</Typography>
          <Typography variant="body2" paragraph>
            Remove it from your watchlist. If you have an open position, the trailing stop will still
            protect it, but no new BUY orders will be placed.
          </Typography>

          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>Why did the system buy/sell without my approval?</Typography>
          <Typography variant="body2" paragraph>
            The system is fully automated. When a high-confidence BUY signal appears for a stock on your
            watchlist, and all risk checks pass, it executes automatically. You receive an email notification
            for every trade. If you want more control, remove stocks from your watchlist to prevent new trades.
          </Typography>

          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>What's the minimum to get started?</Typography>
          <Typography variant="body2" paragraph>
            Add at least 5–10 stocks to your watchlist for diversified signal generation.
            The system works best with a mix of sectors (Technology, Healthcare, Financials, etc.).
          </Typography>

          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>Is this real money?</Typography>
          <Typography variant="body2">
            The system uses Alpaca's paper trading by default — simulated trades with no real money at risk.
            Contact the admin to switch to live trading when you're ready.
          </Typography>
        </AccordionDetails>
      </Accordion>
    </Container>
  );
};

export default Help;
